<template>
  <button
    class="button"
    :data-external="externalId"
    :class="[
      buttonType,

      { 'is-active': showActiveState && active, 'active-enabled': showActiveState, disabled },
    ]"
    @click="!disabled && $emit('click')"
    @mouseover.stop="handleHover(true)"
    @mouseleave.stop="handleHover(false)"
  >
    <span class="button-text">
      <span v-if="$slots.leftIcon" class="button-icon left">
        <slot name="leftIcon" />
      </span>
      <slot />
      <span v-if="$slots.right && (isHovering || active)" class="button-icon right">
        <slot name="right" />
      </span>
    </span>
  </button>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { CategoryButtonType } from '../types/enums/category-button-type';

const {
  buttonType = CategoryButtonType.Primary,
  showActiveState = true,
  externalId,
  active,
  disabled,
} = defineProps<{
  buttonType?: CategoryButtonType;
  showActiveState?: boolean;
  externalId: string;
  active?: boolean;
  disabled?: boolean;
}>();

const emit = defineEmits<{ click: []; hover: [boolean] }>();

const isHovering = ref(false);

const handleHover = (value: boolean) => {
  if (disabled) return;
  isHovering.value = value;
  emit('hover', value);
};
</script>

<style lang="scss" scoped>
$icon-size: 1rem;

.button {
  font-family: var(--default-font-family);
  border-radius: var(--radius-small);
  min-height: 2.5rem;
  min-width: 5.5rem;
  cursor: pointer;
  padding: var(--spacing-50) var(--spacing-100);
  position: relative;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  font-size: var(--paragraph-medium-font-size);
  line-height: var(--paragraph-medium-line-height);
  color: var(--base-blue);
  transition: all 0.15s ease-in-out;

  &.disabled {
    cursor: default;
  }

  &:focus:not([data-focus-visible-added]) {
    outline: none;
  }

  .button-text {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .button-icon {
    width: $icon-size;
    height: $icon-size;

    :deep(svg) {
      width: 100%;
      height: 100%;
      max-height: $icon-size;
    }

    &.left {
      margin-right: var(--spacing-50);
    }

    &.right {
      margin-left: var(--spacing-50);
    }
  }

  &.primary {
    background-color: var(--gray-96);
    border: 1px solid var(--gray-80);

    &:not(.disabled) {
      &:not(.is-active) {
        &:hover,
        &:focus {
          background-color: var(--gray-94);
        }
      }
    }
  }

  &.secondary {
    background-color: var(--white);
    border: 1px solid var(--base-blue);

    &.disabled {
      border-color: var(--gray-60);
      color: var(--gray-60);
    }

    &:not(.disabled) {
      &:hover,
      &:focus {
        background-color: var(--gray-96);
      }
    }
  }

  &.is-active {
    background-color: var(--blue-95);
    border-color: var(--blue-45);
  }
}
</style>
